// Grouping for the ticket list: rows are bucketed by product line, with a
// trailing "Unassigned" section for tickets that have none.
//
// The list loads in slices as you scroll, and grouping runs over *everything
// loaded so far* rather than over one slice. That is the whole point: grouping
// per slice made each product line reappear — and each slice end with its own
// Unassigned block — every time you paged (cTKT-00234).

/** Bucket key for tickets with no product line. Also the product-line filter's
 *  sentinel for "no product line", so the two stay in step. */
export const UNASSIGNED_KEY = 'none';

/** The only field grouping reads. Ticket rows carry it, but the shared Ticket
 *  type doesn't declare it, so it's read structurally rather than required. */
export interface GroupableTicket {
  productLine?: { id: string; name: string; color: string } | null;
}

export interface TicketGroup<T> {
  key: string;
  name: string;
  color: string | null;
  tickets: T[];
}

/** Order the product lines are configured in; only `id` is read. */
export interface ProductLineOrder {
  id: string;
}

/**
 * Bucket `tickets` by product line.
 *
 * Group order: active product lines in their configured order, then any line
 * not in that list (deleted or deactivated, but still attached to tickets)
 * alphabetically, then Unassigned — always last. Within a group the incoming
 * order is preserved, so the server's sort still holds.
 */
export function groupTicketsByProductLine<T>(
  tickets: readonly T[],
  productLines: readonly ProductLineOrder[],
): TicketGroup<T>[] {
  const buckets = new Map<string, TicketGroup<T>>();

  for (const t of tickets) {
    const pl = (t as GroupableTicket).productLine;
    const key = pl?.id ?? UNASSIGNED_KEY;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { key, name: pl?.name ?? 'Unassigned', color: pl?.color ?? null, tickets: [] };
      buckets.set(key, bucket);
    }
    bucket.tickets.push(t);
  }

  const order = new Map(productLines.map((pl, i) => [pl.id, i]));
  return Array.from(buckets.values()).sort((a, b) => {
    if (a.key === UNASSIGNED_KEY) return 1;
    if (b.key === UNASSIGNED_KEY) return -1;
    const ai = order.get(a.key);
    const bi = order.get(b.key);
    if (ai !== undefined && bi !== undefined) return ai - bi;
    if (ai !== undefined) return -1;
    if (bi !== undefined) return 1;
    return a.name.localeCompare(b.name);
  });
}

/** Section headers are noise when nothing loaded has a product line at all. */
export function hasProductLineGroups<T>(groups: readonly TicketGroup<T>[]): boolean {
  return groups.some((g) => g.key !== UNASSIGNED_KEY);
}
